import { getInvoices, type Invoice, type InvoiceStatus } from '@/api/invoices';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@/components/ui';
import { useMatchBankTransaction } from '@/hooks/use-bank-transactions';
import { useToast } from '@/hooks/use-toast';
import { formatCurrency } from '@/lib/formatters';
import type { BankTransaction } from '@kgc/api-contract';
import { useTranslation } from '@kgc/i18n';
import { useEffect, useState, type FormEvent } from 'react';

import { bankMatchErrorToastKey } from './bank-match-error';

interface BankMatchModalProps {
  transaction: BankTransaction;
  onClose: () => void;
  onMatched: () => void;
}

/** L1 párosítható-szűrő (Zeno-ACK): nyitott, fizetésre váró státuszok — státuszonként külön lekérve. */
const MATCHABLE_STATUSES: readonly InvoiceStatus[] = ['SENT', 'OVERDUE', 'PARTIALLY_PAID'];

/**
 * Bank-tétel ↔ számla párosítás modal (35-kör Fázis-2, money-write).
 *
 * A párosítható számlák a `getInvoices` L1-szűrőjéből (nyitott státuszok, FE-filter —
 * Zeno-ACK; az R-5 dedikált matchable-endpoint L2-defer). Submit →
 * `useMatchBankTransaction` → siker: success-toast + `onMatched` refetch + bezárás;
 * hiba: a 6 strukturált guard-code (`BANK_MATCH_*`) → `bankMatchErrorToastKey` toast
 * (code-alapú, message-független).
 */
export function BankMatchModal({ transaction, onClose, onMatched }: BankMatchModalProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { match, isLoading } = useMatchBankTransaction();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [invoiceId, setInvoiceId] = useState('');
  const [matchedAmount, setMatchedAmount] = useState(String(transaction.amount));
  const [loadError, setLoadError] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let active = true;
    // M-016 truncation-mitigáció (Zeno-ACK): státuszonként külön lekérés, NEM egy minden-státuszú
    // pageSize-100 + kliens-szűrés (ott a sok PAID elnyomná a régi nyitott OVERDUE-t). A residual
    // (egyetlen státusz >100 nyitott tétel) marad az R-5 szerver-oldali matchable-endpointra (L2).
    void Promise.all(MATCHABLE_STATUSES.map(status => getInvoices({ status, pageSize: 100 })))
      .then(results => {
        if (active) {
          setInvoices(results.flatMap(res => res.data));
          setLoadError(false);
        }
      })
      .catch(() => {
        // fetch-hiba ≠ üres-lista (empty-state vs error-state GAP): ha a getInvoices
        // hibázik, NEM szabad a hamis "nincs számla" empty-state-et mutatni.
        if (active) {
          setInvoices([]);
          setLoadError(true);
        }
      });
    return () => {
      active = false;
    };
  }, [reloadToken]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!invoiceId) {
      return;
    }
    try {
      await match(transaction.id, { invoiceId, matchedAmount: Number(matchedAmount) });
      toast({ title: t('bank.match.success'), variant: 'default' });
      onMatched();
      onClose();
    } catch (err) {
      toast({ title: t(bankMatchErrorToastKey(err)), variant: 'destructive' });
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
    >
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t('bank.match.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={e => void handleSubmit(e)}
            className="space-y-4"
            data-testid="bank-match-form"
          >
            <div>
              <label className="mb-2 block text-sm font-medium" htmlFor="bank-match-invoice">
                {t('bank.match.invoiceLabel')}
              </label>
              <select
                id="bank-match-invoice"
                className="w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-800"
                value={invoiceId}
                onChange={e => setInvoiceId(e.target.value)}
              >
                <option value="">{t('bank.match.invoicePlaceholder')}</option>
                {invoices.map(inv => (
                  <option key={inv.id} value={inv.id}>
                    {inv.invoiceNumber} —{' '}
                    {formatCurrency(Number(inv.grossAmount) - Number(inv.paidAmount))}
                  </option>
                ))}
              </select>
              {loadError ? (
                <div className="mt-1 flex items-center justify-between gap-2">
                  <p className="text-sm text-destructive">{t('bank.match.loadError')}</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setReloadToken(v => v + 1)}
                    data-testid="bank-match-invoices-retry"
                  >
                    {t('common.retry')}
                  </Button>
                </div>
              ) : (
                invoices.length === 0 && (
                  <p className="mt-1 text-sm text-muted-foreground">{t('bank.match.noInvoices')}</p>
                )
              )}
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium" htmlFor="bank-match-amount">
                {t('bank.match.amountLabel')}
              </label>
              <Input
                id="bank-match-amount"
                type="number"
                value={matchedAmount}
                onChange={e => setMatchedAmount(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                disabled={!invoiceId || isLoading}
                data-testid="bank-match-submit"
              >
                {t('bank.match.submit')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
